import { styled } from 'styled-components';
import { api } from 'common';
import { Link as ReactRouterLink, useParams } from 'react-router-dom';

import Highlight from './Highlight';

import { Avatar } from '@vivaahealth/design-system';
import { getPrevisitAsset } from '../../../../../utils';
import {
  getProviderInitials,
  getProviderNameFriendly,
} from '../../../../../utils/provider';
import { formatProviderSpecialties } from 'common/dist/utils/provider';

interface Props {
  inputValue: string;
  suggestion: api.PrevisitProvider.SafeProviderWithPractices;
  handleProviderClick: (
    provider: api.PrevisitProvider.SafeProviderWithPractices,
  ) => void;
}

const SearchProviderItem: React.FC<Props> = ({
  inputValue,
  suggestion,
  handleProviderClick,
}) => {
  const { organizationSlug } = useParams<{
    organizationSlug: string;
  }>();
  const fullName = getProviderNameFriendly(suggestion, [
    'firstName',
    'lastName',
    'prefix',
    'suffix',
  ]);
  const startIndex = fullName.toLowerCase().indexOf(inputValue.toLowerCase());
  const endIndex = startIndex + inputValue.length;
  const providerSpecialties = formatProviderSpecialties(
    suggestion.profile?.specialties,
  );
  return (
    <ProviderLink
      className="text-decoration-none text-white"
      to={`/directory/${organizationSlug}/providers/${suggestion.preference?.slug}`}
      onMouseDown={() => handleProviderClick(suggestion)}
    >
      <ProviderSuggestion>
        <StyledAvatar
          alt={getProviderNameFriendly(suggestion, [
            'firstName',
            'lastName',
            'prefix',
          ])}
          src={
            suggestion.previsitImageUrl
              ? getPrevisitAsset(suggestion.previsitImageUrl)
              : undefined
          }
          initials={getProviderInitials(suggestion)}
        />
        <ProviderInformation>
          <ProviderName>
            {fullName.substring(0, startIndex)}
            <Highlight text={fullName.substring(startIndex, endIndex)} />
            {fullName.substring(endIndex, fullName.length)}
          </ProviderName>
          <ProviderDetails>{providerSpecialties}</ProviderDetails>
        </ProviderInformation>
      </ProviderSuggestion>
    </ProviderLink>
  );
};

export default SearchProviderItem;

const ProviderLink = styled(ReactRouterLink)`
  text-decoration: none !important;
  color: white !important;
  font-size: 14px !important;
`;

const StyledAvatar = styled(Avatar)`
  height: 40px !important;
  width: 40px !important;

  background-color: var(--color-shade-40);
  margin-top: 12px;
`;

const ProviderSuggestion = styled.div`
  cursor: pointer;
  height: 62px;
  display: flex;
  flex-direction: row;
  padding: 0 1rem;

  :focus {
    background-color: var(--color-shade-30);
    outline-width: 0;
  }

  &:hover {
    background-color: var(--color-shade-30);
  }
`;

const ProviderInformation = styled.div`
  margin-top: 1rem;
  margin-left: 1rem;
  display: flex;
  flex-direction: column;
`;

const ProviderName = styled.div`
  font-size: 0.875rem;
  font-weight: var(--weight-bold);
  color: var(--color-shade-80);
`;

const ProviderDetails = styled.div`
  font-size: 0.75rem;
  font-weight: var(--weight-regular);
  color: var(--color-shade-70);
`;
